import {IO, IUrlCheckReport, IUrlCheckRequest} from "/@lib@/commons/io/io.js";
import {Desk} from "/@lib@/commons/desk.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {LANG} from "/@lib@/commons/lang.js";
import {IBasicUniversePointer} from "/@lib@/core/universe.js";
import {CDM} from "/@lib@/commons/utils/cdm.js";
import {ERROR} from "./errorReport.js";
import {FILES} from "/@lib@/commons/io/files.js";

type IUrlsCheckImpl = (reqs: IUrlCheckRequest[], maxConcurrent: number) => Promise<Dict<IUrlCheckReport>>;

type IUrlsCheckSelect = (url: string, reg?: IReg<IBasicUniversePointer>) => IUrlsCheckImpl;

/** Schéma de description d'une ressource distante. */
export interface JRemoteResDesc {
	/** ### Identité */
	/** Url de cette ressource (non aliasée). */
	url?: string

	/** Si JRemoteResDesc.url n'est pas une version figée et qu'il existe une url "permanente" pointant cette version figée précise. */
	permaUrl?: string

	/** ### Caractéristiques physiques de cette ressource, telle que retournée avec une méthode HEAD ou GET simple */
	/** Http status, -1 si server inaccessible ou url forbidden. */
	status?: 200 | 204 /*folder*/ | -1 /*unreachable*/ | 301 /*Redir*/ | 302 /*Redir*/ | 404 /*Not found*/ | number
	/** Content-Type de cette ressource. */
	type?: string
	/** Content-length en octet de cette ressource. */
	length?: number
	/** Taille optimale en largeur pour l'affichage de cette ressource exprimée en "standardCharSize", ~ unité CSS 'ch'. */
	scsW?: number
	/** Taille optimale en hauteur pour l'affichage de cette ressource exprimée en "standardCharSize", ~ unité CSS 'ch'. */
	scsH?: number
	/** Rotation du contenu autorisée (pour print en particulier). */
	rotate?: boolean

	/** ### Description logique de cette ressource et de ce qu'elle représente (ie pas ce qu'elle est) */
	/** Titre de cette ressource, idéalement dans la langue demandée par l'appelant (http header Accept-Language). */
	title?: string
	/** Liste de termes décrivant la ressource regroupés par catégorie. */
	tags?: JRemoteResDescTags
	/** Libellé de la view de cette ressource (pour la différencier de celles proposées dans 'alts' et 'futures'), dans la langue demandée (Accept-Language). */
	label?: string

	/** ### Vues alternatives */
	/** Alternatives disponibles */
	alts?: JRemoteResDescAlt[]
	/** Alternatives non disponibles, mais pourraient l'être potentiellement ou prochainement... */
	futures?: JRemoteResDescFuture[]

	/** ### Autre custom prop. */
	[key: string]: Jsonisable
}

/**
 * Tags "standards":
 * L'intérêt de ces tags est d'aider à choisir la meilleure alternative en fonction de l'usage de l'appelant.
 * Toutes ces catégories sont optionnelles et ne doivent être précisées que si elles peuvent aider à sélectionner une alternative.
 *
 * Tags spécifiques : exemple pour une archive scar Opale et ses différentes alternatives générées :
 * ```
 * 'opale'?: ('ue' | 'ua' | 'uc' | 'scar' | 'monoSco' | 'multiSco' | 'noSol' | 'short')[]
 * ```
 */
export interface JRemoteResDescTags {
	/**
	 * Nature de la ressource accessible par le web: détermine le mode d'affichage et le comportement à adopter.
	 * 'img' | 'video' | 'audio' sont des flux intégrables dans une balise img, video ou audio.
	 * 'embeddable' est un contenu web html dont on sait qu'il est autorisé à être embarqué dans une iframe.
	 * 'site' est un contenu web html que l'on peut afficher dans une window (pas garanti dans une iframe)
	 * 'editor' représente le backoffice d'édition web de cette ressource
	 * 'binary' est un contenu à télécharger
	 * 'pdf' est un contenu au format pdf
	 */
	'web'?: ('embeddable' /*iframe, window*/ | 'site' /*window*/ | 'img' | 'video' | 'audio' | 'binary' /*download*/ | 'pdf' /*pdfviewer*/ | 'editor' /*window*/)[]

	/** Etendue du corps de cette ressource : aperçu visuel, fiche descriptive courte, fiche détaillée avec résumé/aperçu, version courte du document, version complète. */
	'body'?: ('poster' | 'metas' | 'abstract' | 'short' | 'full')[]

	/** Qualité de la ressource, principalement pour les video, mais peut être utilisé pour les images et audio (sauf adapt, spécifique video)*/
	'quality'?: ('lowest' | 'fair' | 'highest' | 'adapt')[]

	/** Choix de la ressource à exploiter préférentiellement pour un usage online ou offline (offline : les ressources sont embarquées quand c'est possible). */
	'dist'?: ('online' | 'offline')[]

	/** Standard supplémentaire respecté par ce contenu (à enrichir...). */
	'standard'?: ('scorm12' | 'scorm2004' | string)[]

	/** Autres categ et tags. */
	[key: string]: string[]
}

/** Alternatives disponibles */
export interface JRemoteResDescAlt {
	/** Path à résoudre avec JRemoteResDescTags.url et à réinterroger pour obtenir les props physiques si nécessaire. */
	path: string
	tags?: JRemoteResDescTags
	label?: string

	[key: string]: Jsonisable
}

/** Alternatives futures ou potentielles */
export interface JRemoteResDescFuture {
	/** Path à résoudre avec JRemoteResDescTags.url et à réinterroger pour obtenir les props physiques si nécessaire. */
	path: string
	state: 'pending' /*construction en cours*/ | 'onFirstCall' /*sera construit au 1er appel*/ | 'onEdit' /*exige une edition de la res*/
	tags?: JRemoteResDescTags
	label?: string

	[key: string]: Jsonisable
}

/**
 * Métas d'une ressource distante dans le contexte du client appelant.
 * Ajout des url "aliasées" pour une persistance résiliente aux changements des domaines hébergeant les serveurs de ressources.
 */
export interface JRemoteResDescFromClient extends JRemoteResDesc {
	/** Url à utiliser pour stockage dans le contexte client si le portail connait cette url. */
	aliasUrl?: string
	/** Url correpondant à permaUrl. À utiliser pour stockage dans le contexte client si le portail connait cette url. */
	aliasPermaUrl?: string

	//A voir les besoins coté serveur, pas accessible du point de vue du client web.
	//internalUrl?: string
	//cacheUrl?: string
}

/**
 * Représente une vue d'une ressource combinant un JRemoteResDesc et une éventuelle JRemoteResDescAlt.
 */
export class RemoteRes {
	url: string

	constructor(public reg: IReg<IBasicUniversePointer>, public resDesc: JRemoteResDesc, public resDescAlt?: JRemoteResDescAlt) {
		this.url = this.resDescAlt ? new URL(this.resDescAlt.path, this.resDesc.url).href : this.resDesc.url;
	}

	/**
	 * Meilleure stratégie de rendu de cette ressource.
	 * @return
	 *  'iframe' : que si on est sûr que le serveur autorise l'embed (via tags.web), sinon on privilégie window.
	 *  'window' et 'download' : devraient se matérialiser par un bouton d'ouverture dans une nouvelle fenetre et de téléchargement.
	 *  null : res non disponible.
	 *  'download' : si bianire ou inconnu
	 */
	async getBestWebRendition(): Promise<'img' | 'audio' | 'video' | 'iframe' | 'window' | 'download' | null> {
		const webTag = (this.resDescAlt || this.resDesc).tags?.web;
		if (webTag) switch (webTag[0]) {
		case "embeddable" :
			return 'iframe';
		case "site" :
		case "editor" :
		case "pdf" :
			return 'window';
		case "binary" :
			return 'download';
		case "img" :
			return 'img';
		case "audio" :
			return 'audio';
		case "video" :
			return 'video';
		}
		//Pas de tag web, on cherche avec le mimeType
		if (this.resDescAlt) await this.fetchResDesc(); //besoin de récupérer les props physiques
		if (!this.statusOk()) return null;
		const ctType = this.resDesc.type;
		if (ctType) {
			if (ctType.startsWith("image/")) return "img";
			if (ctType.startsWith("video/")) return "video";
			if (ctType.startsWith("audio/")) return "audio";
			if (ctType.startsWith("text/")) return "window"; //si on n'est pas sûr de pouvoir embedder dans une iframe, on privilégie window.
			if (ctType.startsWith("application/pdf") || ctType.startsWith("application/xhtml+xml")) return "window";
		}
		return "download";
	}

	getTags(): JRemoteResDescTags | null {
		return (this.resDescAlt || this.resDesc)?.tags;
	}

	statusOk(def: boolean = false): boolean {
		const st = this.resDesc.status;
		if (typeof st === "number") return st >= 200 && st <= 299;
		return def;
	}

	async fetchResDesc(): Promise<this> {
		if (!this.resDescAlt) return this;
		const newResDesc = (await REMOTE.fetchResDesc(this.url, this.reg)) || {url: this.url};
		this.resDesc = newResDesc;
		this.resDescAlt = null;
		this.url = newResDesc.url;
		return this;
	}
}


/**
 * Filtre sur les propriétés d'une resDesc.
 * Si plus d'une des conditions est spécifié dans ce JRemoteResTagsFilter,
 * l'opérateur logique AND est appliqué.
 */
export interface JRemoteResTagsFilter {
	/**
	 * Le tag nommé hasTag[0] existe et contient au moins une des valeurs suivantes.
	 * Si aucune valeur suivante, seule l'existence du tag est évaluée.
	 */
	hasTag?: string[]
	/**
	 * Le contentType commence par au moins une des valeurs du tableau.
	 */
	typeIn?: string[]
	/** Inverse le sous-filtre. */
	not?: JRemoteResTagsFilter
	/** Applique un AND entre les sous-filtres. */
	and?: JRemoteResTagsFilter[]
	/** Applique un OR entre les sous-filtres. */
	or?: JRemoteResTagsFilter[]
	// /** Application du filtre sur n'importequelle alternative. */
	//TODO anyAlt?: JRemoteResTagsFilter utile ?
}

export namespace REMOTE {
	export const CHECKURL_TIMEOUT = 10000;

	/**
	 * Fournit une description de la ressource url.
	 */
	export async function fetchResDesc(url: string, reg: IReg<IBasicUniversePointer>): Promise<JRemoteResDescFromClient | null> {
		const rmt = reg.env.universe.config.remoteContentUrl;
		if (rmt) return rmt.fetchJson<JRemoteResDescFromClient>(IO.qs("cdaction", "GetResDesc", "param", url));
		return null;
	}

	/**
	 * Trouve la meilleure RemoteRes pour une preview à partir d'une JRemoteResDesc.
	 * Privilégie les tags body : ['poster', 'metas', 'abstract', 'short'], sinon le tag quality ['lowest', 'adapt', 'fair'], sinon retourne resDesc.
	 */
	export function remoteResForPreview(reg: IReg<IBasicUniversePointer>, resDesc: JRemoteResDescFromClient): RemoteRes {
		const bestBody = ['poster', 'metas', 'abstract', 'short'];
		const bestBodyAlt = resDesc.alts?.reduce((best, alt) => {
			return !best || bestIdx(bestBody, alt.tags?.body) < bestIdx(bestBody, best?.tags?.body) ? alt : best
		}, null);
		if (bestBodyAlt && bestIdx(bestBody, bestBodyAlt.tags?.body) < bestIdx(bestBody, resDesc.tags?.body)) return new RemoteRes(reg, resDesc, bestBodyAlt);
		const bestQua = ['lowest', 'adapt', 'fair'];
		const bestQuaAlt = resDesc.alts?.reduce((best, alt) => {
			return !best || bestIdx(bestQua, alt.tags?.quality) < bestIdx(bestQua, best.tags?.quality) ? alt : best
		}, null);
		if (bestQuaAlt && bestIdx(bestQua, bestQuaAlt.tags?.quality) < bestIdx(bestQua, resDesc.tags?.quality)) return new RemoteRes(reg, resDesc, bestQuaAlt);
		return new RemoteRes(reg, resDesc);
	}

	/**
	 * Trouve une RemoteRes de type video à partir d'une JRemoteResDesc.
	 * @param prefQuality préférence pour la qualité. Exemple pour une preview :['lowest', 'adapt', 'fair']
	 */
	export function remoteResAsVideo(reg: IReg<IBasicUniversePointer>, resDesc: JRemoteResDescFromClient, prefQuality: ('lowest' | 'fair' | 'highest' | 'adapt')[] = ['lowest', 'adapt', 'fair']): RemoteRes | null {
		const bestAlt = resDesc.alts?.reduce((best, alt) => {
			return alt.tags?.web?.indexOf('video') >= 0 ? !best || bestIdx(prefQuality, alt.tags?.quality) < bestIdx(prefQuality, best.tags?.quality) ? alt : best : best
		}, null);
		const webTag = resDesc.tags?.web;
		if (webTag ? webTag.indexOf('video') >= 0 : resDesc.type?.startsWith("video/")) {
			if (!bestAlt || bestIdx(prefQuality, resDesc.tags?.quality) < bestIdx(prefQuality, bestAlt.tags?.quality)) return new RemoteRes(reg, resDesc);
		}
		return bestAlt ? new RemoteRes(reg, resDesc, bestAlt) : null;
	}

	/**
	 * Trouve une RemoteRes de type audio à partir d'une JRemoteResDesc.
	 * @param prefQuality préférence pour la qualité. Exemple pour une preview :['lowest', 'fair']
	 */
	export function remoteResAsAudio(reg: IReg<IBasicUniversePointer>, resDesc: JRemoteResDescFromClient, prefQuality: ('lowest' | 'fair' | 'highest')[] = ['lowest', 'fair']): RemoteRes | null {
		const bestAlt = resDesc.alts?.reduce((best, alt) => {
			return alt.tags?.web?.indexOf('audio') >= 0 ? !best || bestIdx(prefQuality, alt.tags?.quality) < bestIdx(prefQuality, best.tags?.quality) ? alt : best : best
		}, null);
		const webTag = resDesc.tags?.web;
		if (webTag ? webTag.indexOf('audio') >= 0 : resDesc.type?.startsWith("audio/")) {
			if (!bestAlt || bestIdx(prefQuality, resDesc.tags?.quality) < bestIdx(prefQuality, bestAlt.tags?.quality)) return new RemoteRes(reg, resDesc);
		}
		return bestAlt ? new RemoteRes(reg, resDesc, bestAlt) : null;
	}

	/** Le plus petit index est le meilleur, si pas trouvé : MAX_SAFE_INTEGER. */
	function bestIdx(array: any[] | null, oneOf: any[] | null): number {
		const i = array && oneOf ? array.findIndex((v) => oneOf.indexOf(v) >= 0) : -1;
		return i >= 0 ? i : Number.MAX_SAFE_INTEGER;
	}

	/** Retourne la 1ère view de type editor. */
	export function findEditorInResDesc(resDesc: JRemoteResDesc | null): JRemoteResDesc | JRemoteResDescAlt | null {
		if (resDesc?.tags?.web?.indexOf('editor') >= 0) return resDesc;
		return resDesc?.alts?.find((alt) => alt.tags?.web?.indexOf('editor') >= 0);
	}

	/** Retourne la view la plus complete qui peut s'ouvrir dans une window. */
	export function findFullViewInResDesc(resDesc: JRemoteResDesc | null): JRemoteResDesc | JRemoteResDescAlt | null {
		const bestBody = ["full", "short"];
		return findViewInResDesc(resDesc,
			(v) => v.tags?.web?.indexOf("site") >= 0,
			(v1, v2) => bestIdx(v2.tags?.body, bestBody) < bestIdx(v1.tags?.body, bestBody) ? v2 : v1
		);
	}

	/** Algo générique de sélection de la meilleure vue avec un de critère impératifs à respecter et une fonction de préférence entre 2 vues. */
	export function findViewInResDesc(resDesc: JRemoteResDesc | null, must: (v: JRemoteResDesc | JRemoteResDescAlt) => boolean, best: (v1: JRemoteResDesc | JRemoteResDescAlt, v2: JRemoteResDesc | JRemoteResDescAlt) => JRemoteResDesc | JRemoteResDescAlt): JRemoteResDesc | JRemoteResDescAlt | null {
		if (resDesc == null) return null;
		let result: JRemoteResDesc | JRemoteResDescAlt | null = resDesc.tags && must(resDesc.tags) ? resDesc : null;
		if (resDesc.alts) result = resDesc.alts.reduce((b, c) => must(c) ? b ? best(b, c) : c : b, result);
		return result;
	}

	export function buildUrlFromResDesc(resDesc: JRemoteResDesc, view: JRemoteResDesc | JRemoteResDescAlt | JRemoteResDescFuture | null): string {
		return view && view !== resDesc ? new URL((view as JRemoteResDescAlt | JRemoteResDescFuture).path, resDesc.url).href : resDesc.url;
	}

	export function matchResDesc(filter: JRemoteResTagsFilter, resDesc: JRemoteResDescFromClient, tags: JRemoteResDescTags | null = resDesc.tags): boolean {
		if (filter.typeIn && !FILES.isMimeTypeAllowed(resDesc.type, filter.typeIn)) return false;
		if (filter.hasTag) {
			if (!tags) return false;
			const v = tags[filter.hasTag[0]];
			if (!v) return false;
			const s = filter.hasTag.length;
			if (s > 1) {
				let i = 1;
				while (i < filter.hasTag.length) {
					if (v.indexOf(filter.hasTag[i]) >= 0) break;
					i++;
				}
				if (i >= filter.hasTag.length) return false;
			}
		}
		if (filter.not && matchResDesc(filter.not, resDesc, tags)) return false;
		if (filter.and) for (let i = 0; i < filter.and.length; i++) {
			if (!matchResDesc(filter.and[i], resDesc, tags)) return false;
		}
		if (filter.or) {
			let i = 0;
			while (i < filter.or.length) {
				if (matchResDesc(filter.or[i], resDesc, tags)) break;
				i++;
			}
			if (i >= filter.or.length) return false;
		}
		return true;
	}

	/**
	 * Fournit une url d'accès la ressource url en passant par notre service RemoteContent.
	 * Peut résoudre des pbs d'accès et permet de proposer une fonction de téléchargement
	 * avec l'attribut download car le flux est alors issu du même domaine.
	 */
	export function urlRemoteContent(url: string, reg: IReg<IBasicUniversePointer>): string | null {
		return reg.env.universe.config.remoteContentUrl?.resolve(IO.qs("cdaction", "GetContent", "request", CDM.stringify({url}))).url;
	}

	/**
	 * Migre un ensemble d'URLs par le service remoteContent (en POST)
	 * @param urls Ensemble d'URL à migrer
	 * @param reg Registre à exploiter pour interroger l'univers
	 */
	export async function migrateUrls(urls: string[], reg: IReg<IBasicUniversePointer>) {
		const rmt = reg.env.universe.config.remoteContentUrl;
		if (rmt) {
			try {
				const formData = new FormData();
				formData.append("urls", JSON.stringify(urls));
				return await rmt.fetchJson<string[]>(IO.qs("cdaction", "MigrateUrls"), {method: "POST", body: formData});
			} catch (e) {
				ERROR.log(`Error while migrating urls`, e);
			}
		}
		return urls;
	}

	/**
	 * Migre une URL par le service remoteContent (en GET)
	 * @param urls Ensemble d'URL à migrer
	 * @param reg Registre à exploiter pour interroger l'univers
	 */
	export async function migrateUrl(url: string | null, reg: IReg<IBasicUniversePointer> | null): Promise<string | null> {
		if (url && reg?.env.universe.config.remoteContentUrl) {
			try {
				const result = await reg.env.universe.config.remoteContentUrl.fetchJson<string[]>(IO.qs("cdaction", "MigrateUrls", "urls", CDM.stringify([url])));
				return result ? result[0] || url : url;
			} catch (e) {
				ERROR.log(`Error while migrating the url: ${url}`, e);
			}
		}
		return url;
	}

	/**
	 * Migre une URL par le service remoteContent (en GET)
	 * @param url URL à migrer
	 * @param reg Registre à exploiter pour interroger l'univers
	 */
	export async function aliasUrl(url: string | null, reg: IReg<IBasicUniversePointer> | null): Promise<string | null> {
		if (url && (url.startsWith("http:") || url.startsWith("https:")) && reg?.env.universe.config.remoteContentUrl) {
			try {
				const result = await reg.env.universe.config.remoteContentUrl.fetchJson<string[]>(IO.qs("cdaction", "AliasUrls", "urls", CDM.stringify([url])));
				return result ? result[0] || url : url;
			} catch (e) {
				ERROR.log(`Error while alias the url: ${url}`, e);
			}
		}
		return url;
	}

	/**
	 * Migre plusieurs URLs par le service remoteContent (en GET)
	 * @param urls Ensemble d'URL à migrer
	 * @param reg Registre à exploiter pour interroger l'univers
	 */
	export async function aliasUrls(urls: string[], reg: IReg<IBasicUniversePointer> | null): Promise<string[]> {
		if (reg?.env.universe.config.remoteContentUrl) {
			try {
				const result = await reg.env.universe.config.remoteContentUrl.fetchJson<string[]>(IO.qs("cdaction", "AliasUrls", "urls", CDM.stringify(urls)));
				if (result) {
					for (let i = 0; i < result.length; i++) {
						urls[i] = result[i];
					}
				}
			} catch (e) {
				ERROR.log(`Error while alias the urls: ${urls}`, e);
			}
		}
		return urls;
	}

	/**
	 * Vérifie un ensemble d'URL en sélectionnant les implémentations disponibles (electron, serveur, fetch).
	 *
	 * La sélection de l'implémentation par défaut est surchargable url par url par le svc checkUrlsSelector
	 *
	 * @param urls Ensemble des URLs ou requête (@see IUrlCheckRequest) à vérifier
	 * @param maxConcurrent Nombre d'url à vérifier
	 * @param reg Registre à exploiter pour interroger l'univers
	 */
	export async function checkUrls(reqs: (string | IUrlCheckRequest)[], maxConcurrent: number, reg?: IReg<IBasicUniversePointer>): Promise<Dict<IUrlCheckReport>> {
		const checkUrlsSelector: IUrlsCheckSelect = (reg || REG).reg.getSvc<IUrlsCheckSelect>("checkUrlsSelector") || defaultCheckUrlSelector;

		const reports: Dict<IUrlCheckReport> = {};
		// Tri des requêtes par implémentations
		const checkImplMap = new Map<IUrlsCheckImpl, IUrlCheckRequest[]>();

		const urls = [];
		for (let req of reqs) {
			if (typeof req === "string") req = {url: req};
			if (reg) urls.push(req.url);
		}

		// Appel au service de migration en une seule fois avec l'ensemble des urls
		if (reg) {
			const migrUrls = await migrateUrls(urls, reg);
			for (let i = 0; i < migrUrls.length; i++) {
				(reqs as IUrlCheckRequest[])[i].url = migrUrls[i];
			}
		}

		for (let req of reqs as IUrlCheckRequest[]) {
			const syntaxReport = checkUrlSyntax(req.url);
			if (syntaxReport) reports[req.url] = syntaxReport;
			else {
				const impl = checkUrlsSelector(req.url, reg);
				let implReqs = checkImplMap.get(impl);
				if (!implReqs) checkImplMap.set(impl, implReqs = []);
				implReqs.push(req);
			}
		}

		for (const impl of checkImplMap.keys()) {
			const implReports = await impl(checkImplMap.get(impl), maxConcurrent);
			Object.assign(reports, implReports);
		}
		return reports;
	}

	function defaultCheckUrlSelector(url: string, reg?: IReg<IBasicUniversePointer>): IUrlsCheckImpl {
		if (Desk.electron) return checkUrlsByElectron;
		else return checkUrlsByFetch;
	}

	export async function checkUrl(req: (string | IUrlCheckRequest), reg?: IReg<IBasicUniversePointer>): Promise<IUrlCheckReport> {
		if (typeof req === "string") req = {url: req};
		if (reg) req.url = req.url = await migrateUrl(req.url, reg);
		const syntaxReport = checkUrlSyntax(req.url);
		if (syntaxReport) return syntaxReport;
		else {
			const checkUrlsSelector: IUrlsCheckSelect = (reg || REG.reg).getSvc<IUrlsCheckSelect>("checkUrlsSelector") || defaultCheckUrlSelector;
			const impl = checkUrlsSelector(req.url, reg);
			const reports = await impl([req], 1);
			return reports[req.url];
		}
	}

	/**
	 * API de contrôle d'URL vérifiant la syntaxe et sélectionnant la meilleure implémentation disponible (electron, serveur, fetch).
	 *
	 * @param url URL à vérifier
	 * @param methods? Méthodes à tester si le test par 'HEAD' ne suffit pas, le premier élément devrait être 'HEAD'
	 * @param extractHeaders? En-têtes à retourner dans le rapport.
	 */
	export function checkUrlSyntax(url: string): IUrlCheckReport | null {
		let urlObj;
		try {
			urlObj = new URL(url);
		} catch (e) {
			return {
				status: "invalid",
				desc: "La syntaxe de l'URL est incorrecte."
			}
		}

		if (urlObj.protocol != 'http:' && urlObj.protocol != 'https:') {
			return {
				status: "invalid",
				desc: "Cette URL ne peut être validée (protocole inconnu)."
			}
		}
		return null;
	}

	export async function checkUrlsByElectron(reqs: IUrlCheckRequest[], maxConcurrent: number) {
		// Cumul des timeouts des requêtes + 1s pour le message vers le client
		const timeout = reqs.reduce((acc, req) => acc + (req.timeout || CHECKURL_TIMEOUT), 0) + 1;
		const checkReply = await IO.sendMessage({type: "client:modules:urlCheck:checkUrls", reqs, maxConcurrent}, location.origin, timeout);
		if (checkReply.type == "error") throw new Error(checkReply.msg);
		else return checkReply.reports;
	}

	/**
	 * Contrôle d'URL par l'API fetch
	 *
	 * @see REMOTE.checkUrl
	 */
	export async function checkUrlsByFetch(reqs: IUrlCheckRequest[], maxConcurrent: number): Promise<Dict<IUrlCheckReport>> {
		const reports = await LANG.promisesMap(reqs, checkUrlByFetch, maxConcurrent);
		return LANG.reduceToDict(reports, (index) => reqs[index].url);
	}

	/**
	 * Contrôle d'URL par l'API fetch
	 *
	 * @see REMOTE.checkUrl
	 */
	export function checkUrlByFetch(req: IUrlCheckRequest): Promise<IUrlCheckReport> {
		const abortController = new AbortController();
		const timeoutPromise: Promise<IUrlCheckReport> = new Promise((resolve) => {
			setTimeout(() => {
				abortController.abort();
				resolve({
					status: 'invalid',
					desc: "Délai d'attente dépassé lors de l'accès à cette URL."
				})
			}, req.timeout || CHECKURL_TIMEOUT);
		});

		const method = req.methods ? req.methods[0] : 'HEAD';
		const fallbackMethods = req.methods && req.methods.length > 1 ? req.methods.slice(1) : null;

		const fetchPromise: Promise<IUrlCheckReport> = fetch(req.url, {method, credentials: "same-origin", signal: abortController.signal}).then((resp) => {
			if ((resp.status == 405 || resp.status == 501) && fallbackMethods) {
				const fallbackReq = Object.create(req);
				fallbackReq.methods = fallbackMethods;
				return checkUrlByFetch(fallbackReq);
			}
			const report = IO.reportFromStatus(resp.status);
			if (req.extractHeaders) {
				report.headers = {};
				for (const extractHeader of req.extractHeaders) {
					const headerName = extractHeader.toLowerCase();
					if (headerName in resp.headers) {
						const header = resp.headers.get(headerName);
						if (header) report.headers[extractHeader] = header.split(",");
					}
				}
			}
			return report;
		}).catch(() => {
			return {
				status: 'uncheckable',
				desc: "Impossible de contrôler cette URL depuis un navigateur."
			};
		});
		return Promise.race([fetchPromise, timeoutPromise]);
	}
}
